import React, { useContext, useMemo } from 'react';
import {
    Alert,
    Box,
    Button,
    IconButton,
    MenuItem,
    Stack,
    TextField,
    ThemeProvider,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { Edit, Delete, Visibility, Print } from '@mui/icons-material';
import { MaterialReactTable } from 'material-react-table';
import { Context } from '../../../context/UserContext';
import { getMRTDefaultProps } from '../../../helpers/mrt/getMRTDefaultProps';
import tableThemeCustom from '../../form/tableThemeCustom';
import CustomDialog from '../../layout/CustomDialog';
import ArquivosDetailPanel from './tabs/ArquivosDetailPanel';
import { usePermissoesModule } from './modules/usePermissoesModule';
import { useListagemModule } from './modules/useListagemModule';
import { useRelatorioModule } from './modules/useRelatorioModule';
export default function Index({ tipo = false }) {
    const { parametro } = useContext(Context),
        p = usePermissoesModule(tipo ? 'ArquivoDocumentoTipo' : 'ArquivoDocumento');
    const m = useListagemModule({ tipo, parametro, permissoes: p });
    const relatorio = useRelatorioModule({ mrt: m.mrt, periodo: m.periodo, permissoes: p, tipo });
    const theme = tableThemeCustom(),
        isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const defaults = getMRTDefaultProps({ mrt: m.mrt, parametro, isMobile });
    const columns = useMemo(
        () => [
            {
                accessorKey: 'Empresa.nomeFantasia',
                header: 'Empresa',
                columnFilterModeOptions: ['contains', 'equals', 'startsWith', 'endsWith'],
            },
            ...(tipo
                ? [
                      {
                          accessorKey: 'descricao',
                          header: 'Tipo de documento',
                          columnFilterModeOptions: ['contains', 'equals', 'startsWith', 'endsWith'],
                      },
                  ]
                : [
                      {
                          accessorKey: 'titulo',
                          header: 'Título',
                          columnFilterModeOptions: ['contains', 'equals', 'startsWith', 'endsWith'],
                      },
                      {
                          accessorKey: 'ArquivoDocumentoTipo.descricao',
                          header: 'Tipo',
                          columnFilterModeOptions: ['contains', 'equals'],
                      },
                      {
                          accessorKey: 'dataDocumento',
                          header: 'Data',
                          filterFn: 'equals',
                          columnFilterModeOptions: ['equals'],
                          muiFilterTextFieldProps: { type: 'date', InputLabelProps: { shrink: true } },
                          Cell: ({ cell }) => (cell.getValue() ? cell.getValue().split('-').reverse().join('/') : ''),
                      },
                      {
                          accessorKey: 'quantidadeAnexos',
                          header: 'Anexos',
                          size: 90,
                          enableColumnFilter: false,
                          enableSorting: false,
                          Cell: ({ cell }) => (cell.getValue() == null ? '—' : Number(cell.getValue())),
                      },
                      {
                          accessorKey: 'observacao',
                          header: 'Observações',
                          size: 320,
                          columnFilterModeOptions: ['contains', 'equals'],
                          muiTableBodyCellProps: { sx: { whiteSpace: 'normal', overflowWrap: 'anywhere' } },
                      },
                  ]),
            {
                accessorKey: 'ativo',
                header: 'Ativo',
                filterVariant: 'select',
                filterSelectOptions: [
                    { label: 'Sim', value: '1' },
                    { label: 'Não', value: '0' },
                ],
                filterFn: 'equals',
                columnFilterModeOptions: ['equals'],
                Cell: ({ cell }) => (cell.getValue() ? 'Sim' : 'Não'),
            },
        ],
        [tipo]
    );
    if (!p.autorizado) return <Alert severity={p.pronto ? 'warning' : 'info'}>{p.mensagem}</Alert>;
    return (
        <ThemeProvider theme={theme}>
            <Box sx={{ width: '100%', minWidth: 0 }}>
                <Typography variant="h5" sx={{ mb: 2 }}>
                    {tipo ? 'Tipos de Documento' : 'Arquivo de Documentos'}
                </Typography>
                {m.selecionada && (
                    <CustomDialog
                        title="Excluir cadastro"
                        content={tipo ? 'Excluir este tipo de documento?' : 'Excluir este documento e seus anexos do cadastro?'}
                        onClose={() => {
                            if (!m.excluindo) m.setSelecionada(null);
                        }}
                        onConfirm={m.excluir}
                    />
                )}
                {!tipo && (
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
                        {['dataInicio', 'dataFim'].map((name) => (
                            <TextField
                                key={name}
                                type="date"
                                size="small"
                                label={name === 'dataInicio' ? 'Data inicial do documento' : 'Data final do documento'}
                                value={m.periodo[name]}
                                InputLabelProps={{ shrink: true }}
                                onChange={(e) => m.setPeriodo((prev) => ({ ...prev, [name]: e.target.value }))}
                            />
                        ))}
                        <Button onClick={() => m.setPeriodo({ dataInicio: '', dataFim: '' })}>Limpar período</Button>
                    </Stack>
                )}
                {!tipo && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Expanda a linha para consultar os arquivos do documento.
                    </Typography>
                )}
                {m.erro && (
                    <Alert severity="error" color="text.error" sx={{ mb: 1 }}>
                        {m.erro}
                    </Alert>
                )}
                <MaterialReactTable
                    {...defaults}
                    columns={columns}
                    data={m.dados}
                    getRowId={(r) => r.uuid}
                    enableRowSelection={false}
                    enableGlobalFilterModes={false}
                    enableExpandAll={false}
                    enableExpanding={!tipo}
                    getRowCanExpand={() => !tipo}
                    renderDetailPanel={
                        tipo
                            ? undefined
                            : ({ row }) =>
                                  row.getIsExpanded() ? (
                                      <ArquivosDetailPanel
                                          key={row.original.uuid}
                                          documento={row.original}
                                          permissoes={p}
                                          parametro={parametro}
                                          isMobile={isMobile}
                                      />
                                  ) : null
                    }
                    muiExpandButtonProps={({ row }) => ({
                        title: row.getIsExpanded() ? 'Recolher arquivos' : 'Ver arquivos do documento',
                        'aria-label': row.getIsExpanded() ? 'Recolher arquivos' : 'Ver arquivos do documento',
                    })}
                    muiPaginationProps={{
                        ...defaults.muiPaginationProps,
                        rowsPerPageOptions: (defaults.muiPaginationProps?.rowsPerPageOptions || [5, 10, 20, 50, 100, 200]).filter(
                            (v) => Number(v) <= 200
                        ),
                    }}
                    muiSearchTextFieldProps={{ placeholder: tipo ? 'Pesquisar tipos' : 'Pesquisar título, observações ou tipo' }}
                    renderTopToolbarCustomActions={() => (
                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                            <Button
                                variant="contained"
                                color="secondary"
                                disabled={!p.incluir || m.excluindo}
                                onClick={() => m.abrir()}
                            >
                                Novo
                            </Button>
                            <Button disabled={m.mrt.isLoading || m.mrt.isRefetching || m.excluindo} onClick={m.recarregar}>
                                Atualizar lista
                            </Button>
                            {!tipo && (
                                <Button
                                    startIcon={<Print />}
                                    title="Gerar PDF de todos os documentos dos filtros atuais"
                                    disabled={
                                        !p.imprimir || relatorio.gerando || m.mrt.isLoading || m.mrt.isRefetching || m.excluindo
                                    }
                                    onClick={relatorio.gerar}
                                >
                                    {relatorio.gerando ? 'Gerando PDF...' : 'Relatório PDF'}
                                </Button>
                            )}
                        </Stack>
                    )}
                    renderRowActions={
                        isMobile
                            ? undefined
                            : ({ row }) => (
                                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                      <IconButton
                                          title={p.editar ? 'Abrir / editar' : 'Consultar'}
                                          disabled={m.excluindo}
                                          onClick={() => m.abrir(row.original)}
                                      >
                                          {p.editar ? <Edit /> : <Visibility />}
                                      </IconButton>
                                      <IconButton
                                          title="Excluir"
                                          color="error"
                                          disabled={!p.cancelar || m.excluindo}
                                          onClick={() => m.setSelecionada(row.original)}
                                      >
                                          <Delete />
                                      </IconButton>
                                  </Box>
                              )
                    }
                    renderRowActionMenuItems={
                        isMobile
                            ? ({ row, closeMenu }) => [
                                  <MenuItem
                                      key="abrir"
                                      disabled={m.excluindo}
                                      onClick={() => {
                                          closeMenu();
                                          m.abrir(row.original);
                                      }}
                                  >
                                      Abrir / consultar
                                  </MenuItem>,
                                  <MenuItem
                                      key="excluir"
                                      disabled={!p.cancelar || m.excluindo}
                                      onClick={() => {
                                          closeMenu();
                                          m.setSelecionada(row.original);
                                      }}
                                  >
                                      Excluir
                                  </MenuItem>,
                              ]
                            : undefined
                    }
                />
            </Box>
        </ThemeProvider>
    );
}
